import { useEffect, useRef, useState } from 'react';
import { useBorneStore } from '@/store/borne-store';
import { useCartStore } from '@/store/cart-store';
import { useConfigStore } from '@/store/config-store';
import { useHardwareStore } from '@/store/hardware-store';
import { agentClient } from '@/services/agent-client';
import { offlineQueue } from '@/services/offline-queue';
import { retryManager } from '@/services/retry-manager';
import { transactionRecovery } from '@/services/transaction-recovery';
import { TpeSerial } from '@/hardware/tpe-serial';
import { PrinterUsb } from '@/hardware/printer-usb';
import { PaymentDialog } from '@/components/PaymentDialog';

// ---------------------------------------------------------------------------
// Payment state machine
// ---------------------------------------------------------------------------

type PaymentState =
  | 'connecting'
  | 'waiting_card'
  | 'accepted'
  | 'printing'
  | 'refused'
  | 'cancelled'
  | 'error';

// ---------------------------------------------------------------------------
// PaymentScreen
// ---------------------------------------------------------------------------

export function PaymentScreen() {
  const navigate = useBorneStore((s) => s.navigate);
  const borneId = useConfigStore((s) => s.borneId);
  const clearCart = useCartStore((s) => s.clear);
  const getTotal = useCartStore((s) => s.getTotal);
  const buildTransaction = useCartStore((s) => s.buildTransaction);
  const setTpeStatus = useHardwareStore((s) => s.setTpeStatus);
  const setPrinterStatus = useHardwareStore((s) => s.setPrinterStatus);

  const block = useBorneStore((s) => s.block);

  const [paymentState, setPaymentState] = useState<PaymentState>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const executingRef = useRef(false);
  const tpeRef = useRef<TpeSerial | null>(null);

  const totalCents = Math.round(getTotal() * 100);

  // ---- Main payment flow ----

  async function runPayment() {
    if (executingRef.current) return;
    executingRef.current = true;

    try {
      // 1. Connect / handshake
      setPaymentState('connecting');
      setErrorMessage(undefined);

      const tpe = new TpeSerial();
      tpeRef.current = tpe;

      if (!tpe.isConnected) {
        const connected = await tpe.connect();
        if (!connected) {
          setPaymentState('error');
          setErrorMessage('Impossible de se connecter au terminal de paiement');
          return;
        }
      }

      setTpeStatus('connected');

      const handshakeOk = await tpe.handshake();
      if (!handshakeOk) {
        setPaymentState('error');
        setErrorMessage('Le terminal ne repond pas (handshake echoue)');
        return;
      }

      // 2. Wait for card
      setPaymentState('waiting_card');
      setTpeStatus('busy');

      const result = await tpe.pay(totalCents);
      setTpeStatus('connected');

      // 3. Process result
      if (result.success) {
        setPaymentState('accepted');
        await delay(1500);

        // 4. Submit transaction (with intelligent retry)
        setPaymentState('printing');

        const transactionData = buildTransaction(borneId);
        const cartSnapshot = useCartStore.getState().items;

        // Save pending transaction for crash recovery
        transactionRecovery.save({
          transaction_id: transactionData.transaction_id,
          cart: cartSnapshot,
          total: transactionData.total,
          phoneNumber: transactionData.phone_number,
          status: 'paid',
          createdAt: new Date().toISOString(),
          lastUpdate: new Date().toISOString(),
        });

        let transactionId = transactionData.transaction_id;

        const submitResult = await retryManager.executeWithRetry(
          async () => {
            try {
              const res = await agentClient.submitTransaction(transactionData);
              return {
                success: res.success,
                transaction_id: res.transaction_id,
                errorCode: res.success ? undefined : 'UNKNOWN',
              };
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err);
              return { success: false, error: message, errorCode: 'NETWORK_ERROR' };
            }
          },
          (reason) => block(reason),
        );

        if (submitResult.success && submitResult.transaction_id) {
          transactionId = submitResult.transaction_id;
          transactionRecovery.updateStatus('sent');
        } else if (!submitResult.success) {
          // Offline fallback: enqueue for later sync
          await offlineQueue.enqueue(transactionData);
          transactionRecovery.updateStatus('sent');
        }

        // 5. Poll and print
        try {
          const printData = await agentClient.pollPrintData(transactionId);
          if (printData) {
            const printer = new PrinterUsb();
            if (printer.isConnected) {
              setPrinterStatus('connected');
              await printer.printTicket(printData);
            }
            // Increment ticket counter after successful print
            useConfigStore.getState().incrementTicket();
            transactionRecovery.updateStatus('printed');
          }
        } catch {
          // Print failure is non-blocking — transaction already recorded.
        }

        // 6. Done
        transactionRecovery.clear();
        clearCart();
        navigate('screensaver');
        return;
      }

      // Handle non-success results
      if (result.responseType === 'REFUSED') {
        setPaymentState('refused');
      } else if (result.responseType === 'CANCELLED') {
        setPaymentState('cancelled');
      } else {
        setPaymentState('error');
        setErrorMessage(result.error ?? `Erreur terminal: ${result.responseType}`);
      }
    } catch (err) {
      setPaymentState('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Erreur inconnue',
      );
    } finally {
      executingRef.current = false;
    }
  }

  // Check for recoverable transaction on mount
  useEffect(() => {
    if (transactionRecovery.hasRecoverableTransaction()) {
      setShowRecoveryDialog(true);
    } else {
      runPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRecovery(resume: boolean) {
    setShowRecoveryDialog(false);
    if (resume) {
      const tx = transactionRecovery.load();
      if (tx) {
        const action = transactionRecovery.getRecoveryAction(tx);
        if (action === 'retry_print') {
          setPaymentState('printing');
          try {
            const printData = await agentClient.pollPrintData(tx.transaction_id);
            if (printData) {
              const printer = new PrinterUsb();
              if (printer.isConnected) {
                setPrinterStatus('connected');
                await printer.printTicket(printData);
              }
              useConfigStore.getState().incrementTicket();
              transactionRecovery.updateStatus('printed');
            }
          } catch {
            // Non-blocking
          }
          transactionRecovery.clear();
          clearCart();
          navigate('screensaver');
        } else if (action === 'retry_send') {
          setPaymentState('printing');
          // Already printed, just cleanup
          transactionRecovery.clear();
          clearCart();
          navigate('screensaver');
        } else {
          // cleanup
          transactionRecovery.clear();
          runPayment();
        }
      } else {
        runPayment();
      }
    } else {
      transactionRecovery.clear();
      runPayment();
    }
  }

  // ---- Close handler (retry or go back depending on state) ----

  function handleClose() {
    if (paymentState === 'refused') {
      // Retry payment
      runPayment();
    } else {
      // Go back to products
      navigate('products');
    }
  }

  if (showRecoveryDialog) {
    return (
      <div className="overlay">
        <div className="flex flex-col items-center gap-6 px-8 bg-surface-light rounded-2xl p-8 max-w-md animate-fade-in">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p className="text-xl text-white text-center font-semibold">
            Transaction en cours detectee
          </p>
          <p className="text-gray-400 text-center">
            Une transaction precedente n&apos;a pas ete completee. Voulez-vous la reprendre?
          </p>
          <div className="flex gap-4 w-full">
            <button
              type="button"
              onClick={() => handleRecovery(true)}
              className="flex-1 btn-success py-3"
            >
              Reprendre
            </button>
            <button
              type="button"
              onClick={() => handleRecovery(false)}
              className="flex-1 bg-surface border border-gray-600 text-white font-semibold py-3 rounded-xl"
            >
              Ignorer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PaymentDialog
      state={paymentState}
      error={errorMessage}
      onClose={handleClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
